import { clsx } from 'clsx';
import dayjs from 'dayjs';
import { twMerge } from 'tailwind-merge';

export * from './toast';
export * from './cookie';

export function cn(...inputs) {
	return twMerge(clsx(inputs));
}

export function capitalize([first, ...rest]) {
	return `${first.toUpperCase()}${rest.join('')}`;
}

export function toCamelCase(string) {
	return string.replace(/[_-]([a-z])/g, letter => letter[1].toUpperCase());
}

/**
 * Creates a debounced function that delays invoking func until after wait milliseconds have
 * elapsed since the last time the debounced function was invoked.
 * @param {function} fn - The function to debounce.
 * @param {number} wait - The number of milliseconds to delay.
 * @returns {function} The new debounced function.
 */
export function debounce(fn, wait) {
	let timerID;
	function debounced(...params) {
		const context = this;

		clearTimeout(timerID);
		timerID = setTimeout(fn.bind(context), wait, ...params);
	}

	return debounced;
}

/**
 * Creates a promise function that delays invoking func.
 * @param {function} fn - The function to debounce.
 * @param {number} ms - The number of milliseconds to delay.
 * @returns {function} The new debounced function.
 */
export function delay(ms) {
	return new Promise(resolve => {
		setTimeout(resolve, ms);
	});
}

/**
 * Creates a try-catch with retry.
 * @param {function} fn - The function to execute.
 * @param {number} retries - The number of retries for the function call.
 * @param {number} delayTime - The number of milliseconds to delay
 * @returns {function} The new debounced function.
 */
export async function retryOn404(fn, retries = 8, delayTime = 2000) {
	try {
		const response = await fn();
		return response;
	} catch (error) {
		if (error.status === 404 && retries > 0) {
			await delay(delayTime);
			return retryOn404(fn, retries - 1, delayTime);
		}
		throw error;
	}
}

export const DATE_FORMAT_TYPES = {
	DEFAULT: 'default',
	SHORT: 'short'
};

const DATE_FORMATS_BY_TYPE = {
	[DATE_FORMAT_TYPES.DEFAULT]: 'LL',
	[DATE_FORMAT_TYPES.SHORT]: 'll'
};

export function formatDate(value, type = DATE_FORMAT_TYPES.DEFAULT) {
	const date = dayjs.unix(value);
	const isSpanishShortVersion =
		dayjs.locale() === 'es' && type === DATE_FORMAT_TYPES.SHORT;

	return isSpanishShortVersion
		? capitalize(date.format('MMM D, YYYY'))
		: date.format(DATE_FORMATS_BY_TYPE[type]);
}

export function formatDashDate(value) {
	return dayjs.unix(value).format('L').replace(/\//g, '-');
}

export function formatFileSize(bytes) {
	const sizes = ['Bytes', 'KB', 'MB', 'GB'];
	if (bytes === 0) return '0 Bytes';
	const i = Math.floor(Math.log(bytes) / Math.log(1024));
	const fileSize = (bytes / 1024 ** i).toFixed(1);
	return `${fileSize} ${sizes[i]}`;
}

/**
 * Creates an array of numbers progressing from start up to
 * If end is not specified, it's set to start with start then set to 0.
 * @param {number} [start=0]
 * @param {number} end
 * @example
 * range(2, 6); // [2, 3, 4, 5, 6]
 * range(4); // [0, 1, 2, 3]
 */

export function range(start, end) {
	if (end === undefined) {
		end = start - 1;
		start = 0;
	}

	return Array.from({ length: end - start + 1 }, (v, index) => index + start);
}

export function downloadFileFromUrl(url, filename) {
	const link = document.createElement('a');
	link.href = url;
	link.download = filename;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
}

export function hasPassedDays(from, numDays) {
	const fromDate = dayjs.unix(from);
	const daysAgo = dayjs().subtract(numDays, 'day');

	return fromDate.isBefore(daysAgo);
}
